import React, { useState, useEffect, useCallback, useRef } from "react";
import { Button, Spinner } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import {
  Clock, Flag, ChevronLeft, ChevronRight, Send, AlertTriangle,
} from "lucide-react";
import apiClient from "../../utils/apiClient";
import { ConfirmModal } from "../ui";
import CommonToaster from "../Common/CommonToaster";
import "./StudentAssessments.scss";

// ── Question Renderer ────────────────────────────────────────────────
const QuestionRenderer = ({ question, answer, onAnswer, questionIndex, totalQuestions, onFlag }) => {
  const handleOptionClick = (optionId) => {
    if (question.questionType === "multi_select") {
      const current = answer.selectedOptionIds || [];
      const exists = current.includes(optionId);
      const updated = exists
        ? current.filter((id) => id !== optionId)
        : [...current, optionId];
      onAnswer({ selectedOptionIds: updated });
    } else {
      onAnswer({ selectedOptionIds: [optionId] });
    }
  };

  return (
    <div className="ta-question">
      <div className="ta-question__number">
        <span>Question {questionIndex + 1} of {totalQuestions} · {question.marks || 1} mark(s)</span>
        <button
          type="button"
          className={`btn btn-sm ${answer.isFlagged ? "btn-warning" : "btn-outline-secondary"}`}
          onClick={() => onFlag(!answer.isFlagged)}
        >
          <Flag size={14} className="me-1" /> {answer.isFlagged ? "Flagged" : "Flag"}
        </button>
      </div>

      <div className="ta-question__text">{question.questionText}</div>

      {question.imageUrl && (
        <img src={question.imageUrl} alt="Question" className="ta-question__image" />
      )}

      {question.codeSnippet && (
        <pre className="ta-question__code">{question.codeSnippet}</pre>
      )}

      {["single_choice", "multi_select", "true_false", "image_mcq", "code_conceptual"].includes(question.questionType) && (
        <div className="ta-question__options">
          {(question.options || []).map((opt, idx) => {
            const isSelected = (answer.selectedOptionIds || []).includes(opt._id);
            return (
              <button
                type="button"
                key={opt._id}
                className={`ta-question__option ${isSelected ? "ta-question__option--selected" : ""}`}
                onClick={() => handleOptionClick(opt._id)}
              >
                <span className="ta-question__option-label">
                  {String.fromCodePoint(65 + idx)}
                </span>
                <span className="ta-question__option-text">{opt.text}</span>
                {opt.imageUrl && <img src={opt.imageUrl} alt="" className="ms-2" style={{ maxHeight: 60, borderRadius: 4 }} />}
              </button>
            );
          })}
        </div>
      )}

      {["text_answer", "fill_blank"].includes(question.questionType) && (
        <input
          type="text"
          className="ta-question__text-input"
          placeholder="Type your answer..."
          value={answer.textAnswer || ""}
          onChange={(e) => onAnswer({ textAnswer: e.target.value })}
        />
      )}
    </div>
  );
};

// ── Question Palette ─────────────────────────────────────────────────
const QuestionPalette = ({ answers, currentIndex, onNavigate }) => {
  const answered = answers.filter((a) => a.isAnswered).length;
  const flagged = answers.filter((a) => a.isFlagged).length;

  return (
    <div className="ta-nav">
      <div className="ta-nav__title">Questions</div>
      <div className="ta-nav__grid">
        {answers.map((a, idx) => {
          let cls = "ta-nav__item";
          if (idx === currentIndex) cls += " ta-nav__item--current";
          else if (a.isFlagged) cls += " ta-nav__item--flagged";
          else if (a.isAnswered) cls += " ta-nav__item--answered";
          return (
            <button type="button" key={a.question || idx} className={cls} onClick={() => onNavigate(idx)}>
              {idx + 1}
            </button>
          );
        })}
      </div>
      <div className="ta-nav__legend">
        <span><span className="ta-nav__legend-dot ta-nav__legend-dot--current" /> Current</span>
        <span><span className="ta-nav__legend-dot ta-nav__legend-dot--answered" /> Answered</span>
        <span><span className="ta-nav__legend-dot ta-nav__legend-dot--flagged" /> Flagged</span>
      </div>
      <div className="ta-nav__summary">
        <div><span>Answered</span> <strong>{answered}/{answers.length}</strong></div>
        <div><span>Flagged</span> <strong>{flagged}</strong></div>
        <div><span>Remaining</span> <strong>{answers.length - answered}</strong></div>
      </div>
    </div>
  );
};

// ── Timer ────────────────────────────────────────────────────────────
const formatTime = (seconds) => {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  return `${m}:${String(s).padStart(2, "0")}`;
};

// ── Main Page ────────────────────────────────────────────────────────
const TakeAssessmentPage = () => {
  const { attemptId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [attempt, setAttempt] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [timeLeft, setTimeLeft] = useState(null);
  const [confirmSubmit, setConfirmSubmit] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });

  const startTimeRef = useRef(Date.now());
  const saveTimeoutRef = useRef(null);

  // ── Fetch attempt data ──────────────────────────────────────────
  const fetchAttempt = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get(`/student-assessments/attempt/${attemptId}`);
      setAttempt(data.attempt);
      setQuestions(data.questions);
      setAnswers(data.attempt.answers || []);

      if (data.attempt.expiresAt) {
        const remaining = Math.max(0, Math.floor((new Date(data.attempt.expiresAt) - Date.now()) / 1000));
        setTimeLeft(remaining);
      }
    } catch {
      setToast({ show: true, message: "Failed to load assessment", type: "error" });
    } finally {
      setLoading(false);
    }
  }, [attemptId]);

  useEffect(() => { fetchAttempt(); }, [fetchAttempt]);

  // ── Timer ──────────────────────────────────────────────────────
  useEffect(() => {
    if (timeLeft === null || timeLeft <= 0) return;

    const interval = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          handleSubmit(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeLeft !== null]);

  // ── Anti-cheat: tab switch detection ───────────────────────────
  const handleViolationResponse = useCallback((data, maxSwitches) => {
    if (data.autoSubmitted) {
      setToast({ show: true, message: "Assessment auto-submitted due to violations", type: "error" });
      setTimeout(() => navigate(`/s/assessment/result/${attemptId}`), 1500);
      return;
    }
    setToast({ show: true, message: `Tab switch detected (${data.tabSwitchCount}/${maxSwitches})`, type: "warning" });
  }, [attemptId, navigate]);

  useEffect(() => {
    const antiCheat = attempt?.assessment?.antiCheat;
    if (!antiCheat?.tabSwitchDetection) return;

    const handleVisibility = () => {
      if (!document.hidden) return;
      apiClient.post(`/student-assessments/attempt/${attemptId}/violation`, { type: "tab_switch" })
        .then(({ data }) => handleViolationResponse(data, antiCheat.maxTabSwitches))
        .catch(() => {});
    };

    document.addEventListener("visibilitychange", handleVisibility);
    return () => document.removeEventListener("visibilitychange", handleVisibility);
  }, [attempt, attemptId, handleViolationResponse]);

  // ── Anti-cheat: right click & copy-paste ───────────────────────
  useEffect(() => {
    const antiCheat = attempt?.assessment?.antiCheat;
    if (!antiCheat) return;

    const handlers = [];

    if (antiCheat.disableRightClick) {
      const h = (e) => { e.preventDefault(); };
      document.addEventListener("contextmenu", h);
      handlers.push(() => document.removeEventListener("contextmenu", h));
    }

    if (antiCheat.disableCopyPaste) {
      const h = (e) => { e.preventDefault(); };
      document.addEventListener("copy", h);
      document.addEventListener("paste", h);
      document.addEventListener("cut", h);
      handlers.push(
        () => document.removeEventListener("copy", h),
        () => document.removeEventListener("paste", h),
        () => document.removeEventListener("cut", h)
      );
    }

    return () => handlers.forEach((fn) => fn());
  }, [attempt]);

  // ── Auto-save answer ──────────────────────────────────────────
  const saveAnswer = useCallback(async (questionId, answerData) => {
    try {
      const { data } = await apiClient.put(`/student-assessments/attempt/${attemptId}/answer`, {
        questionId,
        ...answerData,
      });
      if (data.expired) {
        setToast({ show: true, message: "Time expired!", type: "error" });
        setTimeout(() => navigate(`/s/assessment/result/${attemptId}`), 1500);
      }
    } catch {
      // Silent fail for auto-save
    }
  }, [attemptId, navigate]);

  const handleAnswer = useCallback((answerData) => {
    setAnswers((prev) => {
      const updated = [...prev];
      const current = updated[currentIndex];
      if (!current) return prev;

      const newAnswer = { ...current, ...answerData };
      newAnswer.isAnswered = (newAnswer.selectedOptionIds?.length > 0) || (newAnswer.textAnswer?.trim()?.length > 0);
      updated[currentIndex] = newAnswer;

      // Debounced save
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = setTimeout(() => {
        saveAnswer(current.question, answerData);
      }, 500);

      return updated;
    });
  }, [currentIndex, saveAnswer]);

  const handleFlag = useCallback((isFlagged) => {
    setAnswers((prev) => {
      const updated = [...prev];
      if (updated[currentIndex]) {
        updated[currentIndex] = { ...updated[currentIndex], isFlagged };
        saveAnswer(updated[currentIndex].question, { isFlagged });
      }
      return updated;
    });
  }, [currentIndex, saveAnswer]);

  // ── Submit ─────────────────────────────────────────────────────
  const handleSubmit = async (autoSubmit = false) => {
    setSubmitting(true);
    try {
      const timeSpent = Math.floor((Date.now() - startTimeRef.current) / 1000);
      const { data } = await apiClient.post(`/student-assessments/attempt/${attemptId}/submit`, { timeSpent });
      if (data.submitted) {
        navigate(`/s/assessment/result/${attemptId}`);
      }
    } catch (err) {
      setToast({ show: true, message: err.response?.data?.message || "Failed to submit", type: "error" });
    } finally {
      setSubmitting(false);
      setConfirmSubmit(false);
    }
  };

  if (loading) {
    return <div className="text-center py-5"><Spinner animation="border" size="sm" /></div>;
  }

  if (!attempt || !questions.length) {
    return <div className="text-center py-5 text-muted">Assessment not available</div>;
  }

  const currentQuestion = questions[currentIndex];
  const currentAnswer = answers[currentIndex] || {};
  const unansweredCount = answers.filter((a) => !a.isAnswered).length;

  let timerClass = "ta-header__timer";
  if (timeLeft !== null && timeLeft <= 60) timerClass += " ta-header__timer--danger";
  else if (timeLeft !== null && timeLeft <= 300) timerClass += " ta-header__timer--warning";

  return (
    <div className="take-assessment-page">
      {/* Header */}
      <div className="ta-header">
        <div className="ta-header__title">{attempt.assessment?.title}</div>
        {timeLeft !== null && (
          <div className={timerClass}>
            <Clock size={16} /> {formatTime(timeLeft)}
          </div>
        )}
        <div className="ta-header__actions">
          <Button
            variant="danger"
            size="sm"
            onClick={() => setConfirmSubmit(true)}
            disabled={submitting}
          >
            <Send size={14} className="me-1" /> Submit
          </Button>
        </div>
      </div>

      {/* Body */}
      <div className="ta-body">
        <div>
          {currentQuestion && (
            <QuestionRenderer
              question={currentQuestion}
              answer={currentAnswer}
              onAnswer={handleAnswer}
              questionIndex={currentIndex}
              totalQuestions={questions.length}
              onFlag={handleFlag}
            />
          )}

          {/* Navigation */}
          <div className="ta-question__nav">
            <Button
              variant="outline-secondary"
              size="sm"
              disabled={currentIndex === 0}
              onClick={() => setCurrentIndex((i) => i - 1)}
            >
              <ChevronLeft size={14} className="me-1" /> Previous
            </Button>
            <Button
              variant="outline-primary"
              size="sm"
              disabled={currentIndex === questions.length - 1}
              onClick={() => setCurrentIndex((i) => i + 1)}
            >
              Next <ChevronRight size={14} className="ms-1" />
            </Button>
          </div>
        </div>

        <QuestionPalette
          answers={answers}
          currentIndex={currentIndex}
          onNavigate={setCurrentIndex}
        />
      </div>

      <ConfirmModal
        show={confirmSubmit}
        onHide={() => setConfirmSubmit(false)}
        onConfirm={() => handleSubmit(false)}
        title="Submit Assessment"
        message={
          unansweredCount > 0
            ? `You have ${unansweredCount} unanswered question(s). Are you sure you want to submit?`
            : "Are you sure you want to submit this assessment?"
        }
        confirmText={submitting ? "Submitting..." : "Submit"}
        variant="danger"
        icon={<AlertTriangle size={20} />}
      />

      <CommonToaster show={toast.show} message={toast.message} type={toast.type} onClose={() => setToast({ ...toast, show: false })} />
    </div>
  );
};

export default TakeAssessmentPage;
